import { motion } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy } from './Slide7BankExample.copy'

const ease = [0.16, 1, 0.3, 1] as const

export default function Slide7BankExample(_props: SlideProps) {
  const c = useSlideCopy(copy, {
    skipSpellingFor: ['fakeUrl', 'fakeAmount'],
  })
  const VERIFY_ROUTES = [c.verifyRoute1, c.verifyRoute2, c.verifyRoute3]

  return (
    <div className="relative min-h-[60vh] flex flex-col items-center justify-center px-4 mx-auto max-w-5xl">
      <div className="relative grid w-full grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] lg:items-center lg:gap-9">
        {/* Phone mockup */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.45, delay: 0.08, ease }}
          className="mx-auto w-full max-w-sm"
        >
          <div className="relative">
            <div className="absolute -start-[3px] top-24 h-8 w-[2px] rounded-full bg-slate-600/45" />
            <div className="relative overflow-hidden rounded-t-[2.5rem] border-[4px] border-b-0 border-slate-900">
            <div className="bg-slate-50">
              {/* Status bar */}
              <div className="relative flex items-center justify-between px-6 pt-3">
                <span className="text-[13px] font-semibold text-slate-900">9:41</span>
                <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 h-[22px] w-[90px] rounded-full bg-slate-800" />
                <div className="flex items-center gap-1">
                  <svg className="h-3.5 w-3.5 text-slate-900" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="1" y="10" width="3" height="5" rx="0.5" />
                    <rect x="5" y="7" width="3" height="8" rx="0.5" />
                    <rect x="9" y="4" width="3" height="11" rx="0.5" />
                    <rect x="13" y="1" width="3" height="14" rx="0.5" opacity="0.3" />
                  </svg>
                  <svg className="h-3.5 w-3.5 text-slate-900" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 3C5.5 3 3.3 4 1.8 5.6a.75.75 0 001.1 1C4.2 5.3 6 4.5 8 4.5s3.8.8 5.1 2.1a.75.75 0 001.1-1C12.7 4 10.5 3 8 3z" opacity="0.3" />
                    <path d="M8 6.5c-1.7 0-3.2.7-4.3 1.8a.75.75 0 001.1 1C5.7 8.4 6.8 8 8 8s2.3.4 3.2 1.3a.75.75 0 001.1-1C11.2 7.2 9.7 6.5 8 6.5z" opacity="0.6" />
                    <path d="M8 10c-.9 0-1.8.4-2.4 1a.75.75 0 001.1 1c.3-.4.8-.5 1.3-.5s1 .2 1.3.5a.75.75 0 001.1-1C9.8 10.4 8.9 10 8 10z" />
                  </svg>
                  <svg className="h-4.5 w-[22px] text-slate-900" viewBox="0 0 25 12" fill="currentColor">
                    <rect x="0" y="1" width="21" height="10" rx="2" stroke="currentColor" strokeWidth="1" fill="none" />
                    <rect x="22" y="4" width="2" height="4" rx="0.5" opacity="0.4" />
                    <rect x="1.5" y="2.5" width="14" height="7" rx="1" />
                  </svg>
                </div>
              </div>

              {/* Contact header */}
              <div className="px-4 pb-3 pt-2 text-center">
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-slate-500">
                  <svg
                    className="h-6 w-6"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 20a6 6 0 00-12 0" />
                    <circle cx="12" cy="10" r="4" />
                  </svg>
                </div>
                <p className="mt-2 text-[15px] font-semibold text-slate-900">
                  {c.contactName}
                </p>
              </div>
            </div>

            <div className="border-t border-slate-200/80 bg-white px-4 pb-5 pt-4 text-start">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, delay: 0.5, ease }}
                className="mb-2 text-center text-[12px] font-medium text-slate-400"
              >
                {c.todayPrefix}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 22, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.42, delay: 0.62, ease }}
                className="rounded-2xl rounded-es-none bg-slate-200/70 px-4 py-3 origin-top-left rtl:origin-top-right"
              >
                <p className="text-[15px] sm:text-base leading-relaxed text-slate-800">
                  {c.bodyBefore}{c.fakeAmount}{c.bodyAfter}
                  <span className="break-all text-blue-600 underline">
                    {c.fakeUrl}
                  </span>
                </p>
              </motion.div>
            </div>
          </div>
          </div>
        </motion.div>

        {/* Decision tree */}
        <div className="mx-auto w-full max-w-xl lg:max-w-none lg:border-s lg:border-slate-200/80 lg:ps-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 1.02, ease }}
            className="pb-6"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-500">
                <svg
                  className="h-3 w-3"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                >
                  <path d="M3 9L9 3M3 3l6 6" />
                </svg>
              </span>
              <p className="text-xl sm:text-[1.4rem] font-semibold text-slate-800">
                {c.whatTheyWantLabel}
              </p>
            </div>
            <p className="mt-2 ps-9 text-base sm:text-lg leading-relaxed text-slate-500 max-w-md">
              {c.whatTheyWant}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 1.18, ease }}
            className="pt-6"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <svg
                  className="h-3 w-3"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                >
                  <path d="M2.5 6.5L5 9l4.5-6" />
                </svg>
              </span>
              <p className="text-xl sm:text-[1.4rem] font-semibold text-slate-800">
                {c.saferMoveLabel}
              </p>
            </div>
            <p className="mt-2 ps-9 text-base sm:text-lg leading-relaxed text-slate-500">
              {c.saferMove}
            </p>
            <div className="mt-4 space-y-3 ps-9">
              {VERIFY_ROUTES.map((route, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: 1.3 + i * 0.09, ease }}
                  className="flex items-start gap-2.5"
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center text-emerald-600">
                    <svg
                      className="h-3.5 w-3.5"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    >
                      <path d="M2.5 6.5L5 9l4.5-6" />
                    </svg>
                  </span>
                  <p className="text-base sm:text-lg leading-relaxed text-slate-500">
                    {route}
                  </p>
                </motion.div>
              ))}
            </div>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, delay: 1.62, ease }}
              className="mt-5 ps-9 text-base sm:text-lg leading-relaxed text-slate-400"
            >
              {c.saferMoveTrailer}
            </motion.p>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
